import { FilterType } from '@bestseller-bit/frontend-community.bootstrapping.application-shell';
import { alpha, ListItemButton, ListItemText, Stack, styled } from '@mui/material';
import { capitalCase } from 'change-case';
import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Searchbar from 'src/components/molecules/Searchbar';
import { useFilteredStores, useQueryParams } from 'src/hooks';
import { useCountry } from 'src/hooks/country/useCountry';
import { useRedirectToOverview } from 'src/hooks/useRedirectToOverview';
import { StoreModel } from 'src/infrastructure/rest-api/api-types';
import { useBuild } from 'src/utils/buildPathFromStore';

export const StoreSelectionFilter: FC<{
    addFilter: (addition: Array<Omit<FilterType, 'name'>> | Omit<FilterType, 'name'>) => void;
    removeFilter?: (value: string | string[], filterGroup?: string) => void;
}> = ({ addFilter, removeFilter }) => {
    const [search, setSearch] = useState('');
    const { storeId } = useQueryParams();
    const { redirectToOverview } = useRedirectToOverview();
    const [stores] = useFilteredStores();
    const buildStoreUrl = useBuild();
    const navigate = useNavigate();
    const country = useCountry();

    const currentStore = useMemo(() => {
        return stores.find((option) => option.id.toString() === storeId) ?? null;
    }, [stores, storeId]);

    const storeOptions = useMemo(() => {
        const countryOption = {
            label: capitalCase(country ?? ''),
            value: '',
            store: null,
        };

        const options = stores
            .filter((store) => store.storeName?.includesInsensitive(search))
            .map((store) => ({
                label: store.storeName,
                value: store.id.toString(),
                store,
            }))
            .filter((option) => option.label)
            .sort((a, b) => a.label.localeCompare(b.label));

        return [countryOption, ...options];
    }, [stores, country, search]);

    const handleStoreChange = useCallback(
        (store: StoreModel | null) => {
            removeFilter?.('stores', 'stores');

            if (store) {
                addFilter({
                    key: 'stores',
                    value: store.id.toString(),
                    displayName: store.storeName,
                    filterGroup: 'stores',
                });

                const storeUrl = buildStoreUrl(store);
                navigate({
                    pathname: storeUrl.pathname,
                    search: storeUrl.search,
                });
            }
            if (!store) {
                redirectToOverview?.();
            }
        },
        [addFilter, removeFilter, buildStoreUrl, navigate, redirectToOverview]
    );

    const onResetInputField = useCallback(() => {
        setSearch('');
    }, []);

    const onUpdateUserInput = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearch(event.currentTarget.value);
    }, []);

    return (
        <Stack
            sx={{
                gap: 3,
                paddingX: 2,
            }}
        >
            <Searchbar
                placeholder="Search store"
                resetInputField={onResetInputField}
                value={search}
                updateUserInput={onUpdateUserInput}
            />
            <Stack
                sx={{
                    gap: 2,
                }}
            >
                {storeOptions.map((store) => {
                    const isSelected =
                        currentStore?.id.toString() === store.value || (!currentStore && store.value === '');
                    return (
                        <StyledListItemButton
                            selected={isSelected}
                            key={store.store?.id ?? 'all'}
                            onClick={() => handleStoreChange(store.store)}
                        >
                            <StyledListItemText
                                selected={isSelected}
                                slotProps={{
                                    primary: {
                                        variant: 'subtitle2',
                                        sx: { fontWeight: isSelected ? 'bold' : 'normal' },
                                    },
                                }}
                            >
                                {store.label}
                            </StyledListItemText>
                        </StyledListItemButton>
                    );
                })}
            </Stack>
        </Stack>
    );
};
const StyledListItemButton = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== 'selected',
})(({ selected, theme }) => {
    return {
        backgroundColor: selected ? alpha(theme.palette.info.main, 0.15) : 'transparent',
        borderRadius: '4px',
        '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.1) },
        gap: theme.spacing(2),
        alignItems: 'center',
        color: 'inherit',
        padding: theme.spacing(1, 2, 1, 3),
    };
});

const StyledListItemText = styled(ListItemText, {
    shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ selected, theme }) => ({
    color: selected ? theme.palette.info.main : 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}));
